import React, { useEffect, useRef } from 'react';

export interface MobileSelectOption {
	value: string;
	label: string;
}

interface MobileSelectProps {
	label?: string;
	value: string;
	options: MobileSelectOption[];
	onChange: (value: string) => void;
	placeholder?: string;
}

const MobileSelect: React.FC<MobileSelectProps> = ({ label, value, options, onChange, placeholder }) => {
	const [open, setOpen] = React.useState(false);
	const [searchQuery, setSearchQuery] = React.useState('');
	const sheetRef = useRef<HTMLDivElement | null>(null);
	const searchInputRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpen(false);
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, []);

	// Focus search input when modal opens
	useEffect(() => {
		if (open && searchInputRef.current) {
			setTimeout(() => {
				searchInputRef.current?.focus();
			}, 100);
		} else if (!open) {
			setSearchQuery(''); // Reset search when modal closes
		}
	}, [open]);

	// Filter options based on search query
	const filteredOptions = React.useMemo(() => {
		if (!searchQuery.trim()) return options;
		const query = searchQuery.toLowerCase().trim();
		return options.filter(opt => 
			opt.label.toLowerCase().includes(query) || 
			opt.value.toLowerCase().includes(query)
		);
	}, [options, searchQuery]);

	return (
		<div>
			{label && <label className="block text-sm text-gray-700 mb-1">{label}</label>}
			<button
				type="button"
				onClick={() => setOpen(true)}
				className="w-full border rounded px-3 py-2 flex items-center justify-between bg-white"
			>
				<span className={`text-sm ${value ? 'text-gray-900' : 'text-gray-400'}`}>
					{value ? options.find(o => o.value === value)?.label : (placeholder || 'Select')}
				</span>
				<svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
				</svg>
			</button>

			{open && (
				<div className="fixed inset-0 z-[100] flex flex-col justify-end bg-black/50 pb-safe" onClick={() => setOpen(false)}>
					<div ref={sheetRef} className="bg-white rounded-t-2xl shadow-lg max-h-[70vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
						<div className="p-4 border-b flex items-center justify-between">
							<div className="w-6 h-6" />
							<div className="font-semibold">{label || 'Select'}</div>
							<button className="text-primary" onClick={() => setOpen(false)}>Done</button>
						</div>
						
						{/* Search Input */}
						<div className="p-4 border-b">
							<div className="relative">
								<input
									ref={searchInputRef}
									type="text"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									placeholder="Search..."
									className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
								/>
								<svg 
									className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" 
									fill="none" 
									stroke="currentColor" 
									viewBox="0 0 24 24"
								>
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
								</svg>
								{searchQuery && (
									<button
										onClick={() => setSearchQuery('')}
										className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
									>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
										</svg>
									</button>
								)}
							</div>
						</div>

						{/* Options List */}
						<div className="overflow-auto overscroll-contain flex-1">
							{filteredOptions.length === 0 ? (
								<div className="p-4 text-center text-gray-500 text-sm">
									No results found
								</div>
							) : (
								filteredOptions.map(opt => {
									const active = opt.value === value;
									return (
										<button
											key={opt.value}
											onClick={() => { onChange(opt.value); setOpen(false); }}
											className={`w-full text-left px-4 py-3 border-b ${active ? 'bg-primary/10 text-primary font-medium' : 'bg-white hover:bg-gray-50'}`}
										>
											{opt.label}
										</button>
									);
								})
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default MobileSelect;


